
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Calendar, Trophy, Flag, Gavel, Settings, LogOut, Briefcase, Users, Grid, 
  ClipboardList, History, MapPin, Car, Edit, Archive, Newspaper, Menu, X, BarChart2, UserX, 
  LineChart, Award, Crown, Image as ImageIcon, BookOpen, Activity, Tv, FileJson, Upload, Download, Hash, Bug, MessageSquare, Terminal, Server, MessageCircle, Monitor, Video, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import { useAuth } from './AuthContext';
import { useLeague } from './LeagueContext';
import { UserRole } from '../types';
import { API_BASE_URL } from '../config';
import { Button, Card } from './UI'; 
import { apiRequest } from '../utils/api';

export const ICON_MAP: Record<string, any> = {
    LayoutDashboard, Calendar, Trophy, Flag, Gavel, Settings, Briefcase, Users, Grid,
    ClipboardList, History, MapPin, Car, Archive, Newspaper, BarChart2, UserX,
    LineChart, Award, Crown, ImageIcon, BookOpen, Activity, Tv, FileJson, Upload, Download, Hash,
    Bug, Terminal, Server, MessageCircle, Menu, Monitor, Video
};

interface NavItemData {
    id: number; label: string; path: string; icon: string; category: string; allowed_roles: string[]; is_active: boolean;
}

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const { settings, teams, teamThemeEnabled } = useLeague();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const [bugDescription, setBugDescription] = useState('');
  const [sendingBug, setSendingBug] = useState(false);
  const [navItems, setNavItems] = useState<NavItemData[]>([]);
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem('sidebarCollapsed') === '1');
  const sidebarRef = useRef<HTMLElement>(null);

  const userTeam = user?.teamId ? teams.find(t => t.id === user.teamId) : null;
  
  useEffect(() => {
      if (teamThemeEnabled && userTeam && userTeam.color) {
          document.documentElement.style.setProperty('--f1-red', userTeam.color);
      } else if (settings.theme_color) {
          document.documentElement.style.setProperty('--f1-red', settings.theme_color);
      }
  }, [teamThemeEnabled, userTeam, settings.theme_color]);

  useLayoutEffect(() => {
      const saved = sessionStorage.getItem('sidebarScrollPos');
      if (sidebarRef.current && saved) sidebarRef.current.scrollTop = parseInt(saved, 10);
  }, [location.pathname]); 

  useEffect(() => {
      if (user) apiRequest<NavItemData[]>('navigation.php').then(data => { if (Array.isArray(data)) setNavItems(data); }).catch(() => {});
  }, [user]);

  useEffect(() => {
      const root = document.querySelector('.sdc-route-view');
      if (!root) return;

      const iconLabels: Record<string, string> = {
          x: 'Schließen', menu: 'Menü öffnen', edit: 'Bearbeiten', pencil: 'Bearbeiten',
          trash: 'Löschen', 'trash-2': 'Löschen', search: 'Suchen', filter: 'Filtern',
          download: 'Herunterladen', upload: 'Hochladen', save: 'Speichern', plus: 'Hinzufügen',
          'chevron-down': 'Öffnen', 'chevron-up': 'Schließen', 'chevron-left': 'Zurück',
          'chevron-right': 'Weiter', eye: 'Anzeigen', 'eye-off': 'Ausblenden', settings: 'Einstellungen',
          refresh: 'Aktualisieren', 'refresh-cw': 'Aktualisieren', copy: 'Kopieren', info: 'Informationen',
          message: 'Nachricht öffnen', 'message-square': 'Nachricht öffnen', user: 'Benutzer öffnen',
          'user-x': 'Benutzeraktion', bug: 'Fehler melden', bell: 'Benachrichtigungen', calendar: 'Kalender öffnen'
      };

      const enhanceAccessibility = (scope: ParentNode) => {
          scope.querySelectorAll<HTMLElement>('button, a[href]').forEach(control => {
              const hasName = (control.textContent || '').trim() || control.getAttribute('aria-label') || control.getAttribute('aria-labelledby');
              if (hasName) return;
              const iconClass = control.querySelector('svg')?.getAttribute('class') || '';
              const iconName = iconClass.match(/lucide-([a-z0-9-]+)/)?.[1] || '';
              const label = control.getAttribute('title') || iconLabels[iconName] || 'Aktion ausführen';
              control.setAttribute('aria-label', label);
          });

          scope.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea').forEach(field => {
              if (field.labels?.length || field.getAttribute('aria-label') || field.getAttribute('aria-labelledby')) return;
              const fallback = field.getAttribute('placeholder') || field.getAttribute('name') || (field instanceof HTMLSelectElement ? 'Auswahl' : 'Eingabefeld');
              field.setAttribute('aria-label', fallback);
          });

          scope.querySelectorAll<HTMLImageElement>('img').forEach(image => {
              if (!image.hasAttribute('alt')) image.setAttribute('alt', '');
          });

          scope.querySelectorAll<HTMLAnchorElement>('a[target="_blank"]').forEach(link => {
              const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
              rel.add('noopener');
              rel.add('noreferrer');
              link.setAttribute('rel', [...rel].join(' '));
          });
      };

      enhanceAccessibility(root);
      const observer = new MutationObserver(() => enhanceAccessibility(root));
      observer.observe(root, { childList: true, subtree: true });
      return () => observer.disconnect();
  }, [location.pathname]);

  const handleSidebarScroll = () => {
      if (sidebarRef.current) sessionStorage.setItem('sidebarScrollPos', sidebarRef.current.scrollTop.toString());
  };

  const toggleSidebar = () => {
      setCollapsed(current => {
          const next = !current;
          sessionStorage.setItem('sidebarCollapsed', next ? '1' : '0');
          return next;
      });
  };

  const handleBugReport = async () => {
      if (!bugDescription.trim()) return;
      setSendingBug(true);
      try {
          await apiRequest('bugs.php', 'POST', { userId: user?.id, description: bugDescription });
          alert("Fehler gemeldet! Danke."); setBugDescription(''); setShowBugModal(false);
      } catch (e: any) { alert("Fehler: " + e.message); }
      finally { setSendingBug(false); }
  };

  if (!user) return <div className="min-h-screen bg-[#08080d] text-white flex items-center justify-center p-4">{children}</div>;

  const isActive = (path: string) => location.pathname === path;

  const getImageUrl = (url?: string | null) => {
    if (!url) return undefined;
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
    return baseUrl ? `${baseUrl}/${url}` : url;
  };

  const logoUrl = getImageUrl(settings.league_logo_url);
  const teamLogoUrl = userTeam?.logoUrl ? getImageUrl(userTeam.logoUrl) : null;
  
  const userRoleNames = user.roles;
  const navigationItems = navItems.some(item => item.path === '/history') ? navItems : [
      ...navItems,
      { id: -1000, label: 'Historie', path: '/history', icon: 'History', category: 'Saison', allowed_roles: [], is_active: true }
  ];
  const filteredNav = navigationItems.filter(item => {
      if (!item.is_active) return false;
      if (item.allowed_roles.length === 0) return true;
      if (item.allowed_roles.includes('TELEMETRY') && user.has_telemetry_access) return true;
      return item.allowed_roles.some(r => userRoleNames.includes(r as UserRole));
  });

  const groupedNav: Record<string, NavItemData[]> = {};
  filteredNav.forEach(item => {
      const cat = item.category || 'Allgemein';
      if (!groupedNav[cat]) groupedNav[cat] = [];
      groupedNav[cat].push(item);
  });
  const categoryOrder = ['Saison', 'Rennleitung', 'Verwaltung'];
  const activeNavItem = filteredNav.find(item => isActive(item.path));
  const ActivePageIcon = activeNavItem ? (ICON_MAP[activeNavItem.icon] || LayoutDashboard) : LayoutDashboard;
  const compactSidebar = collapsed && !isMobileMenuOpen;

  const NavItem: React.FC<{ to: string; icon: any; label: string }> = ({ to, icon: Icon, label }) => {
    const active = isActive(to);
    return (
        <Link to={to} onClick={() => setIsMobileMenuOpen(false)} 
          title={compactSidebar ? label : undefined}
          aria-current={active ? 'page' : undefined}
          className={`sdc-nav-item group flex items-center gap-3 transition-all duration-200 relative ${
            active 
              ? 'is-active text-white' 
              : 'text-white/60 hover:text-white'
          }`}>
          {Icon && <span className="sdc-nav-icon"><Icon className={`w-[16px] h-[16px] shrink-0 ${active ? 'text-f1-red' : ''}`} /></span>}
          {!compactSidebar && <span className="sdc-nav-label truncate">{label}</span>}
        </Link>
    );
  };

  const SidebarContent = () => (
      <div className="sdc-sidebar-inner flex flex-col h-full">
        {/* Logo */}
        <div className={`sdc-sidebar-brand shrink-0 flex items-center ${compactSidebar ? 'justify-center px-2' : 'gap-2 px-4'}`}>
            {!compactSidebar && <Link to="/" className="flex flex-1 min-w-0 items-center gap-3 group" onClick={() => setIsMobileMenuOpen(false)}>
                {logoUrl ? (
                    <img src={logoUrl} alt={`${settings.league_name} Logo`} className="max-h-10 max-w-full w-auto object-contain transition-transform group-hover:scale-[1.03] duration-300" 
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                ) : (
                    <div className="flex items-center gap-2.5">
                        <div className="sdc-brand-mark">SDC</div>
                        <span className="text-sm font-bold tracking-tight text-white truncate">{settings.league_name}</span>
                    </div>
                )}
            </Link>}
            <button type="button" onClick={toggleSidebar} className="sdc-sidebar-toggle hidden lg:flex shrink-0 items-center justify-center transition-colors" aria-label={collapsed ? 'Navigation ausklappen' : 'Navigation einklappen'} title={collapsed ? 'Navigation ausklappen' : 'Navigation einklappen'}>
                {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
        </div>

        {/* Nav */}
        <nav className="sdc-sidebar-nav flex-1 overflow-y-auto" ref={sidebarRef} onScroll={handleSidebarScroll}>
            {Object.keys(groupedNav).sort((a,b) => {
                const idxA = categoryOrder.indexOf(a);
                const idxB = categoryOrder.indexOf(b);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b);
            }).map(cat => (
                <div key={cat} className="sdc-nav-group">
                    {!compactSidebar && <div className="sdc-nav-category">{cat}</div>}
                    <div className="sdc-nav-list">
                    {groupedNav[cat].map(item => (
                        <NavItem key={item.id} to={item.path} icon={ICON_MAP[item.icon] || LayoutDashboard} label={item.label} />
                    ))}
                    </div>
                </div>
            ))}
        </nav>

        {/* User */}
        <div className="sdc-sidebar-user shrink-0">
          <Link to="/profile" className="sdc-user-card flex items-center gap-2.5 transition-all group">
              <div className="relative">
                  <div className="sdc-user-avatar flex items-center justify-center font-semibold text-white text-[10px] overflow-hidden transition-colors">
                    {user.avatarUrl ? <img src={getImageUrl(user.avatarUrl)} className="w-full h-full object-cover"/> : user.username.substring(0,2).toUpperCase()}
                  </div>
                  {teamThemeEnabled && teamLogoUrl && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#0c0c12] flex items-center justify-center p-0.5">
                          <img src={teamLogoUrl} className="w-full h-full object-contain"/>
                      </div>
                  )}
              </div>
              {!compactSidebar && (
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-white/80 truncate">{user.username}</p>
                    <p className="text-[9px] text-white/25 truncate">{userTeam ? userTeam.name : 'Profil'}</p>
                </div>
              )}
          </Link>
          <button onClick={logout} className="sdc-logout flex items-center w-full gap-2.5 transition-all">
            <LogOut className="w-3.5 h-3.5" />{!compactSidebar && <span className="font-medium">Abmelden</span>}
          </button>
        </div>
      </div>
  );

  return (
    <div className="sdc-app-shell min-h-screen flex flex-col lg:flex-row relative">
      <a href="#sdc-main-content" className="fixed left-4 top-3 z-[200] -translate-y-20 rounded-xl bg-white px-4 py-2 text-xs font-black uppercase tracking-wider text-black shadow-2xl transition-transform focus:translate-y-0">Zum Inhalt springen</a>
      {/* Mobile Header */}
      <div className="sdc-mobile-header lg:hidden px-4 flex justify-between items-center sticky top-0 z-50">
          <Link to="/" className="flex min-w-0 items-center gap-3">
              {logoUrl ? <img src={logoUrl} alt={`${settings.league_name} Logo`} className="h-8 max-w-[8rem] w-auto object-contain" /> : <span className="font-bold text-white text-sm tracking-tight truncate">{settings.league_name}</span>}
              <span className="h-5 w-px bg-white/10" />
              <span className="truncate text-[10px] font-bold uppercase tracking-[0.13em] text-white/35">{activeNavItem?.label || 'Dashboard'}</span>
          </Link>
          <button type="button" aria-label="Navigation öffnen" onClick={() => setIsMobileMenuOpen(true)} className="sdc-mobile-menu-button"><Menu size={21} /></button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
          <div className="sdc-mobile-overlay fixed inset-0 z-[60] lg:hidden" onClick={() => setIsMobileMenuOpen(false)}>
              <div className="sdc-mobile-drawer ml-auto flex h-full w-[min(88vw,22rem)] flex-col animate-fade-in" onClick={event => event.stopPropagation()}>
                <div className="flex justify-end p-3"><button type="button" aria-label="Navigation schließen" onClick={() => setIsMobileMenuOpen(false)} className="sdc-mobile-menu-button"><X size={22} /></button></div>
                <div className="min-h-0 flex-1"><SidebarContent /></div>
              </div>
          </div>
      )}

      {/* Desktop Sidebar */}
      <aside className={`sdc-sidebar hidden lg:flex ${collapsed ? 'w-[4.75rem]' : 'w-[16rem]'} flex-col h-screen sticky top-0 transition-all duration-300 overflow-hidden`}>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main id="sdc-main-content" tabIndex={-1} className="sdc-main flex-1 min-w-0">
          <div className="sdc-topbar hidden lg:flex sticky top-0 z-40 items-center px-7">
              <div className="mx-auto flex w-full max-w-[1500px] items-center justify-between">
                  <div className="flex items-center gap-3"><span className="sdc-topbar-icon"><ActivePageIcon size={16} /></span><div><p className="sdc-topbar-category">{activeNavItem?.category || 'Sunday Driver Cup'}</p><p className="sdc-topbar-title">{activeNavItem?.label || 'Übersicht'}</p></div></div>
                  <Link to="/profile" className="sdc-topbar-profile flex items-center gap-2.5"><span className="hidden xl:inline">{user.username}</span><span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full text-[9px] font-bold">{user.avatarUrl ? <img src={getImageUrl(user.avatarUrl)} alt="" className="h-full w-full object-cover" /> : user.username.substring(0, 2).toUpperCase()}</span></Link>
              </div>
          </div>
          <div className="sdc-route-view mx-auto max-w-[1500px] p-4 pb-24 sm:p-6 lg:p-8 xl:p-10">{children}</div>
      </main>

      {/* Bug Report FAB */}
      <button onClick={() => setShowBugModal(true)} className="fixed bottom-5 right-5 z-50 bg-white/[0.04] hover:bg-f1-red/20 text-white/20 hover:text-f1-red p-2.5 rounded-xl border border-white/[0.04] hover:border-f1-red/30 transition-all duration-300 backdrop-blur-sm" title="Fehler melden">
        <Bug size={18} />
      </button>

      {/* Bug Modal */}
      {showBugModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in">
              <div className="w-full max-w-md bg-[#111118] border border-white/[0.06] rounded-2xl shadow-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/[0.04] flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Bug size={16} className="text-f1-red"/> Fehler melden</h3>
                      <button onClick={() => setShowBugModal(false)} className="text-white/30 hover:text-white p-1 rounded-lg hover:bg-white/[0.04]"><X size={18}/></button>
                  </div>
                  <div className="p-6 space-y-4">
                      <p className="text-[13px] text-white/50">Beschreibe den Fehler oder Verbesserungsvorschlag.</p>
                      <textarea 
                          className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl p-3.5 text-white/90 text-sm h-28 focus:border-f1-red/40 outline-none resize-none placeholder:text-white/15"
                          placeholder="Was ist passiert?"
                          value={bugDescription} onChange={e => setBugDescription(e.target.value)} autoFocus
                      />
                      <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setShowBugModal(false)}>Abbrechen</Button>
                          <Button size="sm" onClick={handleBugReport} disabled={!bugDescription.trim() || sendingBug}>
                              {sendingBug ? 'Sende...' : 'Absenden'}
                          </Button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};
